import { useState, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { useAuthStore } from '@/store/auth'
import { clearGameCache } from '@/api/games'
import type { PlayingEntryWithGame, Friend } from '@/types'
import { GameCover } from '@/components/ui/GameCover'
import { Icon } from '@/components/ui/Icon'
import { Avatar } from '@/components/ui/Avatar'
import { StarRating } from '@/components/ui/StarRating'

interface PlayingCardProps {
  entry: PlayingEntryWithGame
  groupMembers: Friend[]
  onUpdate: (note: string) => void
  onPromoteToPlayed: () => void
  onDemoteToWishlist: () => void
  onRate: (rating: number) => void
  canEdit?: boolean
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

export function PlayingCard({ entry, groupMembers, onUpdate, onPromoteToPlayed, onDemoteToWishlist, onRate, canEdit = true }: PlayingCardProps) {
  const { game } = entry
  const isAdmin = useIsAdmin()
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const [editingNote, setEditingNote] = useState(false)

  const myRating = (user && entry.ratings[user.steamId]) || 0
  const ratingValues = Object.values(entry.ratings).filter((v) => v > 0)
  const avgRating =
    ratingValues.length > 0
      ? Math.round((ratingValues.reduce((s, v) => s + v, 0) / ratingValues.length) * 10) / 10
      : null

  const clearCacheMutation = useMutation({
    mutationFn: () => clearGameCache(game.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['playing'] }),
  })
  const [noteValue, setNoteValue] = useState(entry.note)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  function startEditNote() {
    setNoteValue(entry.note)
    setEditingNote(true)
    setTimeout(() => textareaRef.current?.focus(), 0)
  }

  function saveNote() {
    setEditingNote(false)
    if (noteValue !== entry.note) {
      onUpdate(noteValue)
    }
  }

  return (
    <div className="cg-playing-card">
      <div className="cg-playing-cover">
        <GameCover game={game} size="lg" />
      </div>

      <div className="cg-playing-body">
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg-0)', marginBottom: 2 }}>
            {game.title}
          </div>
          {entry.startedAt && (
            <div style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>
              Started {relativeTime(entry.startedAt)}
            </div>
          )}
        </div>

        {editingNote ? (
          <textarea
            ref={textareaRef}
            value={noteValue}
            onChange={(e) => setNoteValue(e.target.value)}
            onBlur={saveNote}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveNote() }
              if (e.key === 'Escape') { setEditingNote(false); setNoteValue(entry.note) }
            }}
            placeholder="Add a session note…"
            style={{
              width: '100%',
              resize: 'none',
              background: 'var(--bg-2)',
              border: '1px solid var(--accent)',
              borderRadius: 4,
              padding: '7px 10px',
              fontSize: 12,
              color: 'var(--fg-1)',
              fontFamily: 'inherit',
              lineHeight: 1.4,
            }}
            rows={3}
          />
        ) : entry.note ? (
          <div
            className="cg-playing-note"
            onClick={canEdit ? startEditNote : undefined}
            style={{ cursor: canEdit ? 'pointer' : 'default' }}
            title={canEdit ? 'Click to edit' : undefined}
          >
            {entry.note}
          </div>
        ) : null}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            fontSize: 11.5,
            color: 'var(--fg-3)',
          }}
        >
          <span style={{ color: 'var(--fg-2)' }}>Your rating</span>
          <StarRating value={myRating} onChange={onRate} size={13} ariaLabel="Your rating" />
          {avgRating !== null && (
            <span className="cg-avg-wrap" tabIndex={0}>
              · avg <span style={{ color: 'var(--fg-1)', fontWeight: 600 }}>{avgRating.toFixed(1)}</span>{' '}
              ({ratingValues.length} rated)
              <div className="cg-avg-pop" onClick={(e) => e.stopPropagation()}>
                {groupMembers
                  .filter((m) => (entry.ratings[m.id] ?? 0) > 0)
                  .map((member) => (
                    <div key={member.id} className="cg-played-rate-row">
                      <Avatar friend={member} size={18} showRing={false} />
                      <span className="cg-played-rate-name">{member.nickname ?? member.name}</span>
                      <StarRating value={entry.ratings[member.id] ?? 0} readOnly size={12} />
                    </div>
                  ))}
              </div>
            </span>
          )}
        </div>

        <div className="cg-playing-actions">
          {canEdit && (
            <>
              <button className="cg-btn cg-btn-primary" data-action="played" onClick={onPromoteToPlayed}>
                <Icon name="star" size={12} /> Mark played
              </button>
              <button className="cg-btn" data-action="wishlist" onClick={onDemoteToWishlist}>
                <Icon name="wishlist" size={12} />
                Back to wishlist
              </button>
              {!entry.note && !editingNote && (
                <button className="cg-btn" onClick={startEditNote}>
                  <Icon name="edit" size={12} /> Add note
                </button>
              )}
            </>
          )}
          {isAdmin && (
            <button
              className="cg-btn ghost tiny"
              onClick={() => clearCacheMutation.mutate()}
              disabled={clearCacheMutation.isPending}
            >
              {clearCacheMutation.isPending ? 'Clearing…' : 'Clear cache'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
